import { proximityWithinReach } from './proximity';
import { pageObjectInteractionRangeFt } from './object-handling-browser';

type BrowserState = {
    FateTide?: { casterCache?: Map<string, unknown>; objectCache?: Map<string, unknown> };
    SIZE_TYPES?: Array<{ item_id: string; base_size: number }>;
    fetchAndCacheObject?: (id: string) => Promise<unknown>;
    ftResolveCachedInventoryRow?: (id: string) => unknown;
};

/** A creature the page already holds on a cached encounter roster (encounterArr.encounter_creatures_arr) — a give's receiver is
 * usually not in the caster cache, and fetching its whole object (~1–1.5 s) only to learn it is not an inventory row delayed every
 * give (measured 2026-09-22). */
function rosterRow(browser: BrowserState & { FateTide?: { encounterCache?: Map<string, unknown> } }, id: string): unknown {
    const cache = browser.FateTide?.encounterCache;
    if (!(cache instanceof Map)) return undefined;
    for (const encounter of cache.values()) {
        const roster = (encounter as { encounter_creatures_arr?: unknown } | null)?.encounter_creatures_arr;
        for (const row of Array.isArray(roster) ? roster : Object.values(roster && typeof roster === 'object' ? roster : {})) {
            const ec = (row as { encounter_creatures?: { encounter_creature_id?: unknown } } | null)?.encounter_creatures;
            if (String(ec?.encounter_creature_id ?? '').toUpperCase() === id) return row;
        }
    }
    return undefined;
}
export async function getProximityEntity(entityId: string): Promise<unknown> {
    const browser = window as unknown as BrowserState;
    const id = entityId.trim().toUpperCase();
    if (!id) return null;
    return browser.FateTide?.casterCache?.get(id)
        ?? rosterRow(browser, id)
        ?? browser.ftResolveCachedInventoryRow?.(id)
        ?? browser.FateTide?.objectCache?.get(id)
        ?? await browser.fetchAndCacheObject?.(id)
        ?? null;
}

/** Fetch missing facts before deciding. Unknown geometry never permits an interaction. `minReachFt` widens the actor's reach for this
 *  check (object_interaction_range_ft toward an unattended object — inventory-proximity.ts decides when). */
export async function actorCanReachTarget(actorId: string, targetId: string, minReachFt = 0): Promise<boolean | null> {
    const browser = window as unknown as BrowserState;
    try {
        const [actor, target] = await Promise.all([getProximityEntity(actorId), getProximityEntity(targetId)]);
        const size = (id: string): number | null => browser.SIZE_TYPES?.find(entry => entry.item_id.toUpperCase() === id)?.base_size ?? null;
        return actor && target ? proximityWithinReach(actor, target, size, minReachFt) : null;
    } catch {
        return null;
    }
}

/** The actor's object_interaction_range_ft (Psion Telekinetic "Mental Grip", 2026-10-07): 0 when none or the actor is not held. */
export async function actorObjectInteractionRangeFt(actorId: string): Promise<number> {
    try {
        return pageObjectInteractionRangeFt(await getProximityEntity(actorId));
    } catch {
        return 0;
    }
}
